import React, { forwardRef, useRef } from 'react';
import { Calendar } from '../Icons/Calendar';
import DP from 'react-datepicker';
import {
  CustomInput as CustomInputStyled,
  Label,
  InputWrapper,
  ErrorMessage,
  DatePickerStyled,
} from './DatePickerStyles';
import 'react-datepicker/dist/react-datepicker.css';
import { DatePickerHeader } from './DatePickerHeader/DatePickerHeader';

const CustomInput = forwardRef(
  (
    {
      value,
      onClick,
      label,
      isRequired,
      size = 'md',
      hasError,
      disabled,
      ...rest
    }: any,
    ref: any
  ) => (
    <CustomInputStyled>
      {label && (
        <Label>
          {isRequired && <span>*</span>}
          {label}
        </Label>
      )}
      <InputWrapper size={size} disabled={disabled}>
        <input
          disabled={disabled}
          value={value || 'mm/dd/yyyy'}
          onClick={onClick}
          ref={ref}
          {...rest}
        />
        <Calendar />
      </InputWrapper>
    </CustomInputStyled>
  )
);

type Props = {
  selected?: Date | null;
  isRequired?: boolean;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  errorMessage?: string;
  hasError?: boolean;
  style?: any;
  onChange(
    date: Date | [Date | null, Date | null] | null,
    event?: React.SyntheticEvent<any>
  ): void;
  dateFormat?: string;
  showMonthPicker?: boolean;
  showYearPicker?: boolean;
  showMonthYearPicker?: boolean;
  isClearable?: boolean;
  placeholderText?: string;
  testid?: string;
  disabled?: boolean;
  showToday?: boolean;
  onCalendarClose?: () => void;
};

export const DatePicker: React.FC<Props> = ({
  selected,
  label,
  isRequired,
  size,
  errorMessage,
  hasError,
  style,
  dateFormat,
  showMonthYearPicker,
  isClearable,
  placeholderText,
  testid,
  disabled,
  showMonthPicker = true,
  showYearPicker = true,
  showToday = true,
  onChange,
  onCalendarClose,
}) => {
  const ref = useRef<any>(null);
  let errorProps: any = {};
  if (testid) {
    errorProps['data-testid'] = `${testid}-error-message`;
  }

  return (
    <DatePickerStyled className={hasError || errorMessage ? 'error' : ''}>
      <DP
        ref={ref}
        strictParsing
        disabled={disabled}
        placeholderText={placeholderText}
        isClearable={isClearable}
        showMonthYearPicker={showMonthYearPicker}
        dateFormat={dateFormat}
        onChange={onChange}
        selected={selected}
        showMonthDropdown={showMonthPicker}
        showYearDropdown={showYearPicker}
        onMonthChange={onChange}
        onYearChange={onChange}
        renderCustomHeader={(props: any) => (
          <DatePickerHeader
            showYearPicker={showYearPicker}
            showMonthPicker={showMonthPicker}
            onChange={onChange}
            datepickerRef={ref.current}
            {...props}
          />
        )}
        onCalendarClose={onCalendarClose}
        yearDropdownItemNumber={15}
        scrollableYearDropdown
        fixedHeight
        showPopperArrow={false}
        todayButton={showToday ? 'TODAY' : undefined}
        dropdownMode='select'
        customInput={
          <CustomInput
            style={style}
            label={label}
            isRequired={isRequired}
            size={size}
            data-testid={testid}
            disabled={disabled}
          />
        }
      />
      {errorMessage && (
        <ErrorMessage {...errorProps}>{errorMessage}</ErrorMessage>
      )}
    </DatePickerStyled>
  );
};
